import { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

declare const get_form_url: (name: string) => Promise<{ name: string; submitAddr: string }>;

export default function AboutPage() {
  const [formSubmitAddr, setFormSubmitAddr] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    get_form_url('虚空韩漫网 - 联系我们').then((result) => {
      setFormSubmitAddr(result.submitAddr);
    });
  }, []);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formSubmitAddr) return;
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    try {
      const formData = new FormData(form);
      if (honeypotEl) formData.delete('company_alt');
      const res = await fetch(formSubmitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* plain text */ }
      if (res.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed && (parsed as { meta?: { message?: string } }).meta?.message) ||
          (parsed && (parsed as { message?: string }).message) ||
          '';
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于虚空韩漫网',
        url: 'https://szlzjsm.cn/about',
        description:
          '虚空韩漫网作为正规的在线漫画网站，提供正版韩漫大全和免费韩漫下拉式阅读，在尊重版权的同时服务用户。了解我们的品牌故事、信誉保障与联系方式。',
      },
      {
        '@type': 'Organization',
        name: '虚空韩漫网',
        url: 'https://szlzjsm.cn',
        description: '正版韩漫在线阅读平台，提供免费韩漫下拉式阅读服务。',
        email: 'contact@szlzjsm.cn',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@szlzjsm.cn',
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
          hoursAvailable: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '09:00',
              closes: '18:00',
            },
          ],
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://szlzjsm.cn' },
          {
            '@type': 'ListItem',
            position: 2,
            name: '关于我们',
            item: 'https://szlzjsm.cn/about',
          },
        ],
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

          {/* Page Title */}
          <section className="py-10 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
              关于虚空韩漫网
            </h1>
            <p className="text-sm text-foreground-500 mt-3 max-w-2xl mx-auto">
              作为正规的在线漫画网站，虚空韩漫网提供正版韩漫大全和免费韩漫下拉式阅读，在尊重版权的同时服务用户。
            </p>
          </section>

          {/* Brand Introduction */}
          <section className="py-14 md:py-20 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    品牌介绍
                  </h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    虚空韩漫网成立于2024年，是一家专注于韩国漫画（韩漫）在线阅读的正规平台。我们致力于为华语用户提供正版、高质量的韩漫内容，让每一位漫画爱好者都能享受到流畅、舒适的阅读体验。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    我们与多家韩国知名漫画出版社建立了深度合作关系，确保平台上的每一部作品都拥有正版授权。在尊重创作者版权的前提下，我们为用户提供免费的韩漫下拉式阅读服务，让更多人能够便捷地接触到优秀的韩国漫画作品。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    虚空韩漫网的名字寓意着&ldquo;在无限的漫画宇宙中自由探索&rdquo;——我们希望每一位用户都能在这里找到属于自己的漫画世界。
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20modern%20minimalist%20office%20space%20with%20bookshelves%20filled%20with%20colorful%20comic%20books%2C%20warm%20ambient%20lighting%2C%20cozy%20reading%20nook%2C%20artistic%20decor%2C%20Korean%20manhwa%20style%20posters%20on%20wall%2C%20professional%20yet%20creative%20atmosphere&width=600&height=400&seq=about-brand-image&orientation=landscape"
                    alt="虚空韩漫网品牌形象"
                    title="虚空韩漫网 - 品牌形象"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Reputation & Trust */}
          <section className="py-14 md:py-20">
            <div className="max-w-4xl mx-auto text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                信誉保障
              </h2>
              <p className="text-sm text-foreground-500">
                我们深知信任是平台发展的基石，因此将信誉建设放在首位
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '正版授权',
                  desc: '所有漫画均来自正版授权合作，严格尊重创作者版权，坚决抵制盗版内容。',
                },
                {
                  icon: 'ri-lock-line',
                  title: '数据安全',
                  desc: '采用先进的加密技术保护用户隐私，确保您的阅读记录和个人信息安全可靠。',
                },
                {
                  icon: 'ri-speed-up-line',
                  title: '极速加载',
                  desc: '优化的图片加载技术，确保下拉式阅读流畅无卡顿，带来极致阅读体验。',
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: '优质服务',
                  desc: '专业客服团队工作日全天在线，快速响应用户反馈，持续提升服务质量。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Team/Stats */}
          <section className="py-14 md:py-20 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                我们的数字
              </h2>
              <p className="text-sm text-foreground-500 mb-10">用数据见证虚空韩漫网的成长之路</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  { value: '2024', label: '成立年份' },
                  { value: '10,000+', label: '漫画资源' },
                  { value: '50+', label: '合作出版社' },
                  { value: '500万+', label: '月活跃用户' },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
                      {item.value}
                    </p>
                    <p className="text-sm text-foreground-500 mt-1">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="py-14 md:py-20">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们联系
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                    联系方式
                  </h3>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">电子邮件</p>
                        <p className="text-sm text-foreground-500">contact@szlzjsm.cn</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-customer-service-2-line text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">在线客服</p>
                        <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-secondary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">办公地址</p>
                        <p className="text-sm text-foreground-500">中国上海市浦东新区张江高科技园区</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                    发送消息
                  </h3>
                  {formSubmitAddr ? (
                    <form
                      ref={formRef}
                      onSubmit={handleContactSubmit}
                      className="flex flex-col gap-4"
                      data-readdy-form=""
                    >
                      <div className="honeypot-wrapper">
                        <input
                          type="text"
                          name="company_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        required
                        className="px-4 py-3 rounded-lg text-sm bg-background-100 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        required
                        className="px-4 py-3 rounded-lg text-sm bg-background-100 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                      />
                      <textarea
                        name="message"
                        placeholder="请输入您的消息（500字以内）"
                        rows={4}
                        maxLength={500}
                        required
                        className="px-4 py-3 rounded-lg text-sm bg-background-100 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-all resize-none"
                      ></textarea>
                      <button
                        type="submit"
                        className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        发送消息
                      </button>
                    </form>
                  ) : (
                    <div className="text-sm text-foreground-500">正在加载表单...</div>
                  )}
                  {formStatus === 'success' && (
                    <p className="mt-3 text-sm text-green-600">消息已发送成功，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="mt-3 text-sm text-red-500">{formError}</p>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </>
  );
}